import { useCallback, useRef } from 'react';
import { sanitizeReturnTo } from '@auth/utils/sanitizeReturnTo';
import type { LogoutEvent } from '@auth/utils/types';
import { useAuth } from './AuthProvider';

export type UseLogoutOptions = {
  /**
   * Where to land after logout completes when the caller doesn't supply a
   * returnTo. Must be an allowed Peloton URL — the value is run through
   * {@link sanitizeReturnTo} alongside any caller-supplied returnTo.
   *
   * @default '/'
   */
  fallbackReturnTo?: string;

  /**
   * Clear the local Auth0 session only — no `/v2/logout` redirect, so the
   * browser stays on the page and the IdP session survives. The returned
   * `LogoutFn` still deletes the legacy session (when configured) and still
   * resolves once the local cache is dropped; the caller owns any navigation
   * that follows, and `fallbackReturnTo` / the `returnTo` argument are
   * ignored. See `LogoutOpts.localOnly`.
   *
   * @default false
   */
  localOnly?: boolean;

  /**
   * When true, the legacy `peloton_session` cookie still backs server-side
   * rendering and must be invalidated before the Auth0 logout fires.
   *
   * @default false
   */
  legacySessionEnabled?: boolean;

  /**
   * App-supplied function that DELETEs `/auth/session` to clear the legacy
   * session cookie. Only invoked when `legacySessionEnabled` is true.
   * Failures are caught and reported via `onError` — they do not block the
   * Auth0 logout from firing, since the user has already chosen to leave.
   */
  deleteLegacySession?: () => Promise<unknown>;

  /**
   * Optional error reporter for non-fatal legacy-session cleanup failures.
   * Receives an `action` discriminator (currently only `deleteLegacySession`).
   */
  onError?: (error: Error, action: 'deleteLegacySession') => void;

  /**
   * Optional funnel-event hook for observability. Called at the start of
   * the logout flow (`started`), after a successful legacy-session DELETE
   * (`legacy_session_deleted`), and after a failed legacy-session DELETE
   * (`legacy_session_delete_failed`). Consumer apps should pipe these
   * into their analytics sink (e.g. Datadog RUM `addAction`).
   *
   * Note: the eventual Auth0 redirect tears down the page before any
   * post-logout event could be observed reliably, so this hook intentionally
   * stops at the legacy-session step. The `auth.logout.fired` /
   * `auth.logout.error` signals from `AuthProvider`'s `onAuthEvent` cover
   * the Auth0 side. The names emitted here (`started`, `legacy_session_*`)
   * are intentionally disjoint from the AuthProvider names so consumers
   * can pipe both through a shared `auth.logout.${event}` prefix without
   * action-name collisions / double-counting.
   */
  onLogoutEvent?: (event: LogoutEvent, ctx?: Record<string, unknown>) => void;
};

export type LogoutFn = (returnTo?: string) => Promise<void>;

/**
 * @example
 *   const logout = useLogout({
 *     fallbackReturnTo: 'https://www.onepeloton.com',
 *     legacySessionEnabled: true,
 *     deleteLegacySession: () => apiClient.delete('/auth/session'),
 *     onError: reportError,
 *     onLogoutEvent: (ev, ctx) => trackAuthEvent(`auth.logout.${ev}`, ctx),
 *   });
 *
 *   const onSignOutClick = () => logout(window.location.href);
 */
export const useLogout = ({
  fallbackReturnTo = '/',
  localOnly = false,
  legacySessionEnabled = false,
  deleteLegacySession,
  onError,
  onLogoutEvent,
}: UseLogoutOptions = {}): LogoutFn => {
  const { logout } = useAuth();

  // Stash consumer callbacks in refs so an inline arrow on every render
  // doesn't churn the returned LogoutFn identity. Mirrors the pattern in
  // AuthProvider/CallbackHandler.
  const deleteLegacySessionRef = useRef(deleteLegacySession);
  deleteLegacySessionRef.current = deleteLegacySession;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onLogoutEventRef = useRef(onLogoutEvent);
  onLogoutEventRef.current = onLogoutEvent;

  // Observability hook — must never abort logout. If the consumer's
  // handler throws, swallow it: monitoring is best-effort and stranding
  // the user mid-logout is unacceptable.
  const safeEmit = useCallback((event: LogoutEvent, ctx?: Record<string, unknown>) => {
    try {
      onLogoutEventRef.current?.(event, ctx);
    } catch {
      // no-op
    }
  }, []);

  return useCallback<LogoutFn>(
    async returnTo => {
      const sanitizedFallback = sanitizeReturnTo(fallbackReturnTo, '/');
      const sanitizedReturnTo = sanitizeReturnTo(
        returnTo ?? sanitizedFallback,
        sanitizedFallback,
      );

      safeEmit('started', { legacySessionEnabled, localOnly });

      const deleter = deleteLegacySessionRef.current;
      if (legacySessionEnabled && deleter) {
        try {
          await deleter();
          safeEmit('legacy_session_deleted');
        } catch (e) {
          const err = e instanceof Error ? e : new Error(String(e));
          safeEmit('legacy_session_delete_failed', { name: err.name });
          try {
            onErrorRef.current?.(err, 'deleteLegacySession');
          } catch {
            // no-op — consumer error reporter must not break logout.
          }
        }
      }

      if (localOnly) {
        await logout(undefined, { localOnly: true });
        return;
      }

      await logout(sanitizedReturnTo);
    },
    [logout, fallbackReturnTo, localOnly, legacySessionEnabled, safeEmit],
  );
};

export default useLogout;
